'use client';

import React, { useEffect, useRef, useState } from 'react';
import Spline from '@splinetool/react-spline';
import { Box, Flex, Text, HStack, VStack, Image, Button } from '@chakra-ui/react';
import CommonContainer from '../common/CommonContainer';
import { useParams } from 'next/navigation';
import Lottie from "lottie-react";

import MobileLottieJson from '@/assets/lottie/mobile.json'
import MobileLottieJsonForMobile from '@/assets/lottie/mobile/mobile.json'

import Link from 'next/link';

export function LandingPage() {
  const params = useParams();
  const locale = (params?.locale as string) || 'zh-CN';

  // Refs for Lottie animations
  const mobileLottieRef = useRef<any>(null);

  // Refs for containers to observe
  const mobileContainerRef = useRef<HTMLDivElement>(null);
  const marketplaceContainerRef = useRef<HTMLDivElement>(null);

  // State to track if animations have played
  const [mobileHasPlayed, setMobileHasPlayed] = useState(false);
  const [marketplaceHasPlayed, setMarketplaceHasPlayed] = useState(false);

  useEffect(() => {
    // Check if IntersectionObserver is supported
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      // Fallback: play animations immediately if IntersectionObserver is not supported
      mobileLottieRef.current?.play();
      setMarketplaceHasPlayed(true);
      return;
    }

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3, // Trigger when 30% of the element is visible
    };

    const handleMobileIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !mobileHasPlayed) {
          mobileLottieRef.current?.play();
          setMobileHasPlayed(true);
        }
      });
    };

    const handleMarketplaceIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !marketplaceHasPlayed) {
          setMarketplaceHasPlayed(true);
        }
      });
    };

    const mobileObserver = new IntersectionObserver(handleMobileIntersection, observerOptions);
    const marketplaceObserver = new IntersectionObserver(handleMarketplaceIntersection, observerOptions);

    if (mobileContainerRef.current) {
      mobileObserver.observe(mobileContainerRef.current);
    }

    if (marketplaceContainerRef.current) {
      marketplaceObserver.observe(marketplaceContainerRef.current);
    }

    return () => {
      mobileObserver.disconnect();
      marketplaceObserver.disconnect();
    };
  }, [mobileHasPlayed, marketplaceHasPlayed]);

  return (
    <>
      {/* Section 1: Hero */}
      <Box minWidth={1200} pt={25} pb={20} w="full" overflow="hidden">
        {/* Hero Content */}
        <Box
          position="relative"
          h="740px"
          pt="100px"
          pb="160px"
          w="full"
        >
          {/* Background Image */}
          <Box position="absolute" inset="0" overflow="hidden" pointerEvents="none" transform="translateY(210px)">
            <Spline
              scene="https://prod.spline.design/ZJhmipjWKJ3QDG8K/scene.splinecode"
            />
          </Box>

          <CommonContainer>
            <VStack gap="220px" position="relative" zIndex={1}>
              {/* Main Text */}
              <VStack gap="48px">
                <VStack gap="24px">
                  <Text
                    fontSize="64px"
                    lineHeight="1.2"
                    textAlign="center"
                    color="white"
                    fontWeight="medium"
                  >
                    <Text
                      as="span"
                      style={{
                        background: 'linear-gradient(to right, #50d2c1, #c2f3f7)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      Agentic Trading{' '}
                    </Text>
                    <Text
                      as="span"
                      style={{
                        background: 'linear-gradient(to right, #50d2c1, #c2f3f7)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      Terminal{' '}
                    </Text>
                    <Text as="span">with</Text>
                    <br />
                    <Text as="span"> an Open MCP Market</Text>
                  </Text>
                </VStack>

                <VStack gap="8px" color="#9f9f9f" textAlign="center">
                  <Text fontSize="16px" fontWeight="medium">
                    Trading is complex, with too many tools to manage — charts, bots, data feeds, backtests.
                  </Text>
                  <Text
                    fontSize="24px"
                    fontWeight="bold"
                    style={{
                      background: 'linear-gradient(270deg, #50d2c1, #c2f3f7)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Agents are replacing them all.
                  </Text>
                </VStack>
              </VStack>

              {/* Bottom Section */}
              <Flex gap="8px" w="full" align="center">
                {/* Left Description */}
                <VStack flex="1" align="flex-start" fontSize="14px"  lineHeight="1.3">
                  <Text color="#9f9f9f">
                    Wildmeta builds the <Text as="span" fontWeight="semibold" color="white">x402 MCP</Text> open market with a
                  </Text>
                  <Text color="#9f9f9f">
                    <Text as="span" fontWeight="semibold" color="white">mobile trading terminal</Text> that links trading tools, and
                  </Text>
                  <Text color="#9f9f9f">data providers into one composable infrastructure, and</Text>
                  <Text color="#9f9f9f">
                    build the foundation for <Text as="span" fontWeight="semibold" color="white">fully autonomous, on-chain trading.</Text>
                  </Text>
                </VStack>

                {/* Right Buttons */}
                <HStack gap="8px">
                  <Link
                    href={`/${locale}/app`}
                  >
                    <Button
                      bg="white"
                      color="black"
                      h="44px"
                      w="150px"
                      fontSize="14px"
                      fontWeight="medium"
                      borderRadius="4px"
                      _hover={{ bg: 'gray.200' }}
                    >
                      Wildmeta APP
                    </Button>
                  </Link>
                  
                  <Link
                    href={`/${locale}/marketplace`}
                  >
                    <Button
                      bg="black"
                      color="white"
                      h="44px"
                      w="150px"
                      fontSize="14px"
                      fontWeight="medium"
                      borderRadius="4px"
                      border="1px solid white"
                      _hover={{ bg: 'gray.900' }}
                    >
                      Marketplace
                    </Button>
                  </Link>
                  
                  <Link
                    href={`/${locale}/document`}
                  >
                    <Button
                      bg="black"
                      color="white"
                      h="44px"
                      w="150px"
                      fontSize="14px"
                      fontWeight="medium"
                      borderRadius="4px"
                      border="1px solid white"
                      _hover={{ bg: 'gray.900' }}
                    >
                      Documentation
                    </Button>
                  </Link>
                </HStack>
              </Flex>
            </VStack>
          </CommonContainer>
        </Box>
      </Box>

      {/* Section 2: Wildmeta AI Companion */}
      <CommonContainer bg="#0A0B0A">
        <Flex minWidth="810px" gap={{ base: "40px", xl: "80px" }} justify="space-between" align="flex-start" py="120px">
          {/* Left Content */}
          <VStack flex={{ base: 5, xl: 3 }} align="flex-start" gap="60px">
            {/* Title */}
            <Text
              fontSize={{ base: "32px", xl: "48px" }}
              lineHeight="1.3"
              fontWeight="medium"
              
              style={{
                background: 'linear-gradient(270deg, #50d2c1 32.611%, #c2f3f7 65.174%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Wildmeta AI — Your Intelligent Trading Companion
            </Text>

            {/* Description */}
            <VStack align="flex-start" gap={{ base: "24px", xl: "40px" }} fontSize={{ base: "14px", xl: "16px" }} lineHeight="1.3" pr={{ base: "0", xl: "43px"}}>
              <Text color="#9f9f9f">
                Powered by X402 and the MCP Skill Network, Wildmeta AI connects you to a world of{' '}
                <Text as="span" fontWeight="medium" color="white">
                  modular trading strategies.{' '}
                </Text>
              </Text>

              <Text color="#9f9f9f">
                It can execute trades automatically, generate custom strategies, and deliver real-time position insights — acting as your{' '}
                <Text as="span" fontWeight="medium" color="white">
                  personal trading advisor
                </Text>
                {' '}and{' '}
                <Text as="span" fontWeight="medium" color="white">
                  autonomous agent.
                </Text>
              </Text>
            </VStack>
          </VStack>

          {/* Right Video */}
          <Box flex={5} minW="0" ref={mobileContainerRef}>
            <Lottie
              lottieRef={mobileLottieRef}
              animationData={MobileLottieJson}
              autoplay={false}
              loop={false}
              renderer="svg"
              rendererSettings={{
                preserveAspectRatio: 'xMidYMid slice',
                progressiveLoad: true,
                hideOnTransparent: true,
                className: 'lottie-animation',
              }}
              style={{
                width: '100%',
                willChange: 'auto',
                transform: 'translateZ(0)',
              }}
            />
          </Box>
        </Flex>
      </CommonContainer>

      {/* Section 3: MarketPlace */}
      <CommonContainer >
        <Flex minWidth="810px" py="120px" gap={{ base: "40px", xl: "80px" }} justify="space-between" align="flex-start">
          {/* Left Video */}
          <Box
            flex={5}
            position="relative"
            minW="0"
            aspectRatio="2"
            ref={marketplaceContainerRef}
          >
            {marketplaceHasPlayed && (
              <Box
                position="absolute"
                inset="-30%"
              >
                <Spline
                  scene="https://prod.spline.design/xVXnYy3TWGoChnC8/scene.splinecode "
                />
              </Box>
            )}
          </Box>

          {/* Left Content */}
          <VStack flex={{ base: 5, xl: 3 }}  align="flex-start" gap="60px">
            {/* Title */}
            <Text
              fontSize={{ base: "32px", xl: "48px" }}
              lineHeight="1.3"
              fontWeight="medium"
              
              style={{
                background: 'linear-gradient(270deg, #50d2c1 32.611%, #c2f3f7 65.174%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Wildmeta Marketplace
            </Text>

            {/* Description */}
            <VStack align="flex-start" gap={{ base: "24px", xl: "40px" }} fontSize={{ base: "14px", xl: "16px" }} lineHeight="1.3" pr={{ base: "0", xl: "43px"}}>
              <Text color="#9f9f9f">
                Powered by X402 and AI agents, Wildmeta connects both sides of the trading ecosystem — enabling developers to{' '}
                <Text as="span" fontWeight="medium" color="white">
                  monetize their skills
                </Text>
                and users to access{' '}
                <Text as="span" fontWeight="medium" color="white">
                  personalized AI-driven{' '}
                </Text>
                trading solutions.{' '}
              </Text>

              <Link
                href={`/${locale}/marketplace`}
              >
                <Button
                  bg="black"
                  color="white"
                  h="44px"
                  w="185px"
                  fontSize="14px"
                  fontWeight="medium"
                  borderRadius="4px"
                  border="1px solid white"
                  _hover={{ bg: 'gray.900' }}
                >
                  Explore Marketplace
                </Button>
              </Link>
            </VStack>
          </VStack>
        </Flex>
      </CommonContainer>

      {/* Section 4: Tech Architecture */}
      <CommonContainer bg="#0A0B0A">
        <Box
          py="120px"
        >
          <VStack align="center" justify="center" gap="80px">
            <Text
              fontSize="48px"
              fontWeight="bold"
              textAlign="center"
              style={{
                background: 'linear-gradient(270deg, #50d2c1 32.611%, #c2f3f7 65.174%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Tech Architecture
            </Text>

            {/* Architecture Diagram - This is complex, using a placeholder */}
            <Image
              w="906px"
              src="/images/marketplace/framework.png"
            />
          </VStack>
        </Box>
      </CommonContainer>
    </>
  );
}

export function LandingPageMobile() {
  const params = useParams();
  const locale = (params?.locale as string) || 'zh-CN';

  // Refs for Lottie animations
  const mobileLottieRef = useRef<any>(null);
  const marketplaceLottieRef = useRef<any>(null);

  // Refs for containers to observe
  const mobileContainerRef = useRef<HTMLDivElement>(null);
  const marketplaceContainerRef = useRef<HTMLDivElement>(null);

  // State to track if animations have played
  const [mobileHasPlayed, setMobileHasPlayed] = useState(false);
  const [marketplaceHasPlayed, setMarketplaceHasPlayed] = useState(false);

  useEffect(() => {
    // Check if IntersectionObserver is supported
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      // Fallback: play animations immediately if IntersectionObserver is not supported
      mobileLottieRef.current?.setSpeed(0.8);
      mobileLottieRef.current?.play();

      marketplaceLottieRef.current?.setSpeed(0.8);
      marketplaceLottieRef.current?.play();

      return;
    }

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3, // Trigger when 30% of the element is visible
    };

    const handleMobileIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !mobileHasPlayed) {
          mobileLottieRef.current?.setSpeed(0.8);
          mobileLottieRef.current?.play();

          setMobileHasPlayed(true);
        }
      });
    };

    const handleMarketplaceIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !marketplaceHasPlayed) {
          marketplaceLottieRef.current?.setSpeed(0.8);
          marketplaceLottieRef.current?.play();
          
          setMarketplaceHasPlayed(true);
        }
      });
    };

    const mobileObserver = new IntersectionObserver(handleMobileIntersection, observerOptions);
    const marketplaceObserver = new IntersectionObserver(handleMarketplaceIntersection, observerOptions);

    if (mobileContainerRef.current) {
      mobileObserver.observe(mobileContainerRef.current);
    }

    if (marketplaceContainerRef.current) {
      marketplaceObserver.observe(marketplaceContainerRef.current);
    }

    return () => {
      mobileObserver.disconnect();
      marketplaceObserver.disconnect();
    };
  }, [mobileHasPlayed, marketplaceHasPlayed]);

  return (
    <>
      <Box position="relative" py={10} px={4} overflow="hidden">
        {/* Background Image */}
        <Box position="absolute" inset="0" overflow="hidden" pointerEvents="none" transform="translateY(50px)">
          <Spline
            scene="https://prod.spline.design/ZJhmipjWKJ3QDG8K/scene.splinecode"
          />
        </Box>

        <VStack position="relative" zIndex={1} mb={6}>
          <Text
            fontSize="1.75rem"
            lineHeight="1.2"
            color="#fff"
            textAlign="center"
            fontWeight={500}
          >
            <Text
              as="span"
              style={{
                background: 'linear-gradient(to right, #50d2c1, #c2f3f7)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Agentic Trading{' '}
            </Text>
            <Text
              as="span"
              style={{
                background: 'linear-gradient(to right, #50d2c1, #c2f3f7)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Terminal{' '}
            </Text>
            <Text as="span">with an Open MCP Market</Text>
          </Text>

          <VStack mb="8.75rem">
            <Text fontSize="sm" color='#9F9F9F' textAlign="center">
              Trading is complex, with too many tools to manage — charts, bots, data feeds, backtests.
            </Text>
            <Text
              fontSize="sm"
              fontWeight="bold"
              style={{
                background: 'linear-gradient(270deg, #50d2c1, #c2f3f7)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Agents are replacing them all.
            </Text>
          </VStack>

          {/* Left Description */}
          <VStack align="flex-start" mb={6}>
            <Text fontSize="xs" lineHeight="1.3" textAlign="center">
              <Text color="#9f9f9f">
                Wildmeta builds the <Text as="span" fontWeight="semibold" color="white">x402 MCP</Text> open market with a
              </Text>
              <Text color="#9f9f9f">
                <Text as="span" fontWeight="semibold" color="white">mobile trading terminal</Text> that links trading tools, and data
              </Text>
              <Text color="#9f9f9f">providers into one composable infrastructure, and build the</Text>
              <Text color="#9f9f9f">
                foundation for <Text as="span" fontWeight="semibold" color="white">fully autonomous, on-chain trading.</Text>
              </Text>
            </Text>
          </VStack>


          {/* Right Buttons */}
          <HStack gap={2}>
            <Link
              href={`/${locale}/app`}
            >
              <Button
                bg="white"
                color="black"
                h="2.75rem"
                w="7rem"
                fontSize="sm"
                fontWeight="medium"
                borderRadius="4px"
                _hover={{ bg: 'gray.200' }}
              >
                Wildmeta APP
              </Button>
            </Link>
            
            <Link
              href={`/${locale}/marketplace`}
            >
              <Button
                bg="black"
                color="white"
                h="2.75rem"
                w="7rem"
                fontSize="sm"
                fontWeight="medium"
                borderRadius="4px"
                border="1px solid white"
                _hover={{ bg: 'gray.900' }}
              >
                Marketplace
              </Button>
            </Link>
            
            <Link
              href={`/${locale}/document`}
            >
              <Button
                bg="black"
                color="white"
                h="2.75rem"
                w="7rem"
                fontSize="sm"
                fontWeight="medium"
                borderRadius="4px"
                border="1px solid white"
                _hover={{ bg: 'gray.900' }}
              >
                Documentation
              </Button>
            </Link>
          </HStack>
        </VStack>
      </Box>

      <VStack
        bg="#0A0B0A"
        py="3.75rem"
        px={4}
        gap={10}
      >
        <Text
          fontSize="2xl"
          fontWeight="bold"
          textAlign="left"
          style={{
            background: 'linear-gradient(270deg, #50d2c1 32.611%, #c2f3f7 65.174%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          Wildmeta AI — Your Intelligent Trading Companion
        </Text>

        {/* Description */}
        <VStack align="flex-start" gap={6} fontSize="sm" lineHeight="1.3">
          <Text color="#9f9f9f">
            Powered by X402 and the MCP Skill Network, Wildmeta AI connects you to a world of{' '}
            <Text as="span" fontWeight="medium" color="white">
              modular trading strategies.{' '}
            </Text>
          </Text>

          <Text color="#9f9f9f">
            It can execute trades automatically, generate custom strategies, and deliver real-time position insights — acting as your{' '}
            <Text as="span" fontWeight="medium" color="white">
              personal trading advisor
            </Text>
            {' '}and{' '}
            <Text as="span" fontWeight="medium" color="white">
              autonomous agent.
            </Text>
          </Text>
        </VStack>

        <Box w="full" ref={mobileContainerRef}>
          <Lottie
            lottieRef={mobileLottieRef}
            animationData={MobileLottieJsonForMobile}
            autoplay={false}
            loop={false}
            renderer="svg"
            rendererSettings={{
              preserveAspectRatio: 'xMidYMid slice',
              progressiveLoad: true,
              hideOnTransparent: true,
              className: 'lottie-animation',
            }}
            style={{
              width: '100%',
              willChange: 'auto',
              transform: 'translateZ(0)',
            }}
          />
        </Box>
      </VStack>

      <VStack
        bg="#000"
        py="3.75rem"
        px={4}
        gap={10}
      >
        <Text
          w="full"
          fontSize="2xl"
          fontWeight="bold"
          textAlign="left"
          style={{
            background: 'linear-gradient(270deg, #50d2c1 32.611%, #c2f3f7 65.174%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          Wildmeta Marketplace
        </Text>

        {/* Description */}
        <VStack align="flex-start" gap={6} fontSize="sm" lineHeight="1.3">
          <Text color="#9f9f9f">
            Powered by X402 and AI agents, Wildmeta connects both sides of the trading ecosystem — enabling developers to{' '}
            <Text as="span" fontWeight="medium" color="white">
              monetize their skills
            </Text>
            and users to access{' '}
            <Text as="span" fontWeight="medium" color="white">
              personalized AI-driven{' '}
            </Text>
            trading solutions.{' '}
          </Text>

          <Link
            href={`/${locale}/marketplace`}
          >
            <Button
              bg="black"
              color="white"
              h="2.75rem"
              w="11.5625rem"
              fontSize="sm"
              fontWeight="medium"
              borderRadius="4px"
              border="1px solid white"
              _hover={{ bg: 'gray.900' }}
            >
              Explore Marketplace
            </Button>
          </Link>
        </VStack>

        <Box w="full" ref={marketplaceContainerRef} position="relative">
          <Image 
            w="full"
            src="/images/marketplace/mobile.png"
          />
        </Box>
      </VStack>

      <Box
        bg="#0A0B0A"
        py="3.75rem"
        px={4}
      >
        <VStack align="center" justify="center" gap="10">
          <Text
            fontSize="2xl"
            fontWeight="bold"
            textAlign="center"
            style={{
              background: 'linear-gradient(270deg, #50d2c1 32.611%, #c2f3f7 65.174%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Tech Architecture
          </Text>

          {/* Architecture Diagram - This is complex, using a placeholder */}
          <Image 
            w="22.375rem"
            src="/images/marketplace/framework.png"
          />
        </VStack>
      </Box>
    </>
  )
}